import {
    parseSketchEntityReferenceRecords,
} from '../../reference';
import type {SketchEntityReferenceRecord} from '../../reference';
import type {PuppySketchEntityReferenceExtension} from '../../../types/export';

export type {PuppySketchEntityReferenceExtension} from '../../../types/export';

/** assembly.json.extensions 中保存转换实体引用的字段名。 */
export const PUPPY_SKETCH_ENTITY_REFERENCE_EXTENSION_KEY = 'sketchEntityReferences';

/** 转换实体引用扩展协议版本。 */
export enum PuppySketchEntityReferenceExtensionSchemaVersion {
    /** 第一版：保存完整文档级引用 Record。 */
    V1 = 'puppy-sketch-entity-reference-extension-v1',
}

/**
 * 判断未知值是否为离线扩展信封对象。
 *
 * @param value 未知值。
 * @returns 是否为可读取的信封对象。
 */
function isExtensionEnvelope(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 按离线包确定性规则排列已由转换实体模块校验的 Record。
 *
 * @param records 完整引用 Record。
 * @returns 按 referenceId 字典序排列的新数组。
 */
function sortReferenceRecords(
    records: readonly SketchEntityReferenceRecord[],
): SketchEntityReferenceRecord[] {
    return [...records].sort((left, right) => left.referenceId.localeCompare(right.referenceId));
}

/**
 * 根据完整引用 Record 创建离线包扩展。
 *
 * @param records 当前文档的引用 Record；没有引用时可省略。
 * @returns 有引用时返回按 referenceId 排序的扩展，否则返回 `undefined`。
 */
export function createPuppySketchEntityReferenceExtension(
    records: readonly SketchEntityReferenceRecord[] | null | undefined,
): PuppySketchEntityReferenceExtension | undefined {
    if (!records || records.length === 0) {
        return undefined;
    }

    return {
        schemaVersion: PuppySketchEntityReferenceExtensionSchemaVersion.V1,
        records: sortReferenceRecords(parseSketchEntityReferenceRecords(records)),
    };
}

/**
 * 解析未知的转换实体引用扩展。
 *
 * 扩展只负责版本信封与确定性顺序；Record 的完整结构和集合不变量由转换实体模块统一校验。
 *
 * @param value 未知 JSON 值。
 * @returns 已校验并深拷贝的扩展。
 */
export function parsePuppySketchEntityReferenceExtension(
    value: unknown,
): PuppySketchEntityReferenceExtension {
    if (!isExtensionEnvelope(value)) {
        throw new Error('PuppyCAD 离线包转换实体引用扩展必须是对象');
    }
    if (value.schemaVersion !== PuppySketchEntityReferenceExtensionSchemaVersion.V1) {
        throw new Error(`不支持的 PuppyCAD 离线包转换实体引用扩展版本: ${String(value.schemaVersion)}`);
    }
    if (!Array.isArray(value.records)) {
        throw new Error('PuppyCAD 离线包转换实体引用扩展 records 必须是数组');
    }

    return {
        schemaVersion: PuppySketchEntityReferenceExtensionSchemaVersion.V1,
        records: sortReferenceRecords(parseSketchEntityReferenceRecords(value.records)),
    };
}

/**
 * 从 assembly.json extensions 读取转换实体引用扩展。
 *
 * @param extensions assembly.json.extensions。
 * @returns 存在时返回扩展，否则返回 `undefined`。
 */
export function readPuppySketchEntityReferenceExtensionFromAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
): PuppySketchEntityReferenceExtension | undefined {
    const value = extensions?.[PUPPY_SKETCH_ENTITY_REFERENCE_EXTENSION_KEY];
    return value === undefined ? undefined : parsePuppySketchEntityReferenceExtension(value);
}

/**
 * 把转换实体引用扩展写入 assembly.json extensions。
 *
 * @param extensions 原始扩展字段。
 * @param referenceExtension 转换实体引用扩展。
 * @returns 写入后的扩展字段；没有引用时返回原扩展副本。
 */
export function writePuppySketchEntityReferenceExtensionToAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
    referenceExtension: PuppySketchEntityReferenceExtension | undefined,
): Record<string, unknown> | undefined {
    const nextExtensions = extensions ? {...extensions} : {};
    if (referenceExtension) {
        nextExtensions[PUPPY_SKETCH_ENTITY_REFERENCE_EXTENSION_KEY] = referenceExtension;
    } else {
        delete nextExtensions[PUPPY_SKETCH_ENTITY_REFERENCE_EXTENSION_KEY];
    }
    return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined;
}
